// Conjugation Trainer — prompts a verb + pronoun in a chosen tense and checks
// the typed answer (accent-insensitive). Ported from the legacy views.trainer
// with three transformations: window.PORTUGUESE_DATA.* → direct PT imports, the
// global `app` → a mount-element parameter, and the legacy localStorage
// gamification hook (recordAttempt) removed — scoring is session-only.
//
// Spaced-review bridge: a drill IS a retrieval attempt, so each graded answer
// can feed the Leitner schedule. To stay true to the "pin is the on-ramp"
// model (and avoid flooding the rotation with every verb a user idly drills),
// we only auto-roll the schedule for verbs already in the workspace; an
// untracked verb gets a quiet, opt-in "Add to review" affordance instead.
import {
  ALL_VERBS,
  TOP_50_VERBS,
  conjugate,
  PRONOUNS,
  TENSES,
  TENSE_LABELS,
} from "../data/index.js";
import { navigate } from "../router/router.js";
import { isAuthed } from "../lib/auth.js";
import { fetchVerbs, fetchWorkspace, recordReview } from "../lib/data.js";

// Session-only trainer state (was a module global in the legacy app).
let trainerState = null;

// Lazily-loaded SRS context, shared across questions in a session:
//   infToId — infinitive → verb record id (PB id online, `local:<inf>` offline),
//             so trainer-fed rows key the SAME way review.js resolves verbs.
//   tracked — set of verb item_ids the user already has a workspace row for.
// Stays null for guests; review writes are skipped entirely in that case.
let srsCtx = null;

async function ensureSrsContext() {
  if (srsCtx || !isAuthed()) return;
  try {
    const [verbs, workspace] = await Promise.all([fetchVerbs(), fetchWorkspace()]);
    const infToId = new Map(verbs.map((v) => [v.infinitive, v.id]));
    const tracked = new Set(
      workspace.filter((w) => w.collection_type === "verbs").map((w) => w.item_id)
    );
    srsCtx = { infToId, tracked };
  } catch {
    // Backend unreachable — keep drills working as pure practice, no SRS writes.
    srsCtx = { infToId: new Map(), tracked: new Set() };
  }
}

// Resolve a verb's review key. Falls back to the offline id shape so a verb
// drilled while the backend is briefly down still matches review.js later.
function verbItemId(inf) {
  return srsCtx?.infToId.get(inf) || `local:${inf}`;
}

export function trainerView(app, params = {}) {
  app.innerHTML = `
    <section>
      <h2 class="text-2xl font-bold text-sage-800 mb-1">Conjugation Trainer</h2>
      <p class="text-sage-600 text-sm mb-6">Pick a tense, get prompted with a verb + pronoun, and type the correct form.</p>

      <div class="bg-white rounded-2xl border border-sage-100 p-4 mb-4 flex flex-wrap gap-2 items-center">
        <span class="text-sm font-medium text-sage-700 mr-2">Tenses:</span>
        ${TENSES.map(
          (t) => `
          <label class="flex items-center gap-1.5 text-sm cursor-pointer">
            <input type="checkbox" class="tense-cb accent-sage-500" value="${t}" checked />
            ${TENSE_LABELS[t]}
          </label>
        `
        ).join("")}
        <span class="text-sm font-medium text-sage-700 ml-4 mr-2">Scope:</span>
        <select id="scope-sel" class="border border-sage-200 rounded-lg px-2 py-1 text-sm bg-white">
          <option value="top50">Top 50 verbs</option>
          <option value="all">All verbs</option>
          <option value="irregular">Irregular only</option>
          <option value="regular">Regular only</option>
          ${params.verb ? `<option value="single" selected>Just "${params.verb}"</option>` : ""}
        </select>
        <button id="start-btn" class="ml-auto bg-sage-500 hover:bg-sage-600 text-white px-4 py-2 rounded-lg text-sm font-medium">
          ${trainerState ? "Restart" : "Start"}
        </button>
      </div>

      <div id="trainer-area"></div>
    </section>
  `;

  const startBtn = app.querySelector("#start-btn");
  startBtn.addEventListener("click", () => {
    const tenses = Array.from(app.querySelectorAll(".tense-cb"))
      .filter((c) => c.checked)
      .map((c) => c.value);
    if (!tenses.length) {
      alert("Pick at least one tense.");
      return;
    }
    const scope = app.querySelector("#scope-sel").value;
    let pool;
    if (scope === "single" && params.verb) pool = ALL_VERBS.filter((v) => v.inf === params.verb);
    else if (scope === "top50") pool = TOP_50_VERBS;
    else if (scope === "irregular") pool = ALL_VERBS.filter((v) => v.group !== "regular");
    else if (scope === "regular") pool = ALL_VERBS.filter((v) => v.group === "regular");
    else pool = ALL_VERBS;
    trainerState = { pool, tenses, score: 0, attempts: 0, current: null };
    nextQuestion(app);
  });

  if (params.verb) {
    app.querySelector("#scope-sel").value = "single";
    startBtn.click();
  }

  // Warm the SRS context in the background so the first graded answer can
  // already tell whether the verb is tracked. Never blocks the drill.
  ensureSrsContext();

  window.scrollTo({ top: 0, behavior: "smooth" });
}

function nextQuestion(app) {
  const ts = trainerState;
  const verb = ts.pool[Math.floor(Math.random() * ts.pool.length)];
  const tense = ts.tenses[Math.floor(Math.random() * ts.tenses.length)];
  const person = Math.floor(Math.random() * 6);
  const { forms } = conjugate(verb.inf, tense);
  ts.current = { verb, tense, person, answer: forms[person] };

  const area = app.querySelector("#trainer-area");
  area.innerHTML = `
    <div class="bg-white rounded-2xl border border-sage-100 p-6 sm:p-8">
      <div class="flex items-center justify-between mb-4">
        <p class="text-xs text-sage-500 uppercase tracking-wide">${TENSE_LABELS[tense]}</p>
        <p class="text-xs text-sage-500">${ts.score}/${ts.attempts} correct</p>
      </div>
      <div class="text-center mb-6">
        ${verb.emoji ? `<div class="text-5xl mb-3">${verb.emoji}</div>` : ""}
        <p class="text-sage-500 text-sm">Conjugate</p>
        <p class="text-3xl font-bold text-sage-800 mb-1">${verb.inf}</p>
        <p class="text-sage-500 text-sm">${verb.en}</p>
        <p class="mt-4 text-xl text-sage-700">for <b>${PRONOUNS[person]}</b></p>
      </div>
      <form id="answer-form" class="flex gap-2">
        <input id="answer-input" type="text" autocomplete="off" autocapitalize="off"
          class="flex-1 px-4 py-3 border border-sage-200 rounded-xl focus:border-sage-500 focus:ring-2 focus:ring-sage-200 outline-none text-lg"
          placeholder="Type the conjugation…" />
        <button type="submit" class="bg-sage-500 hover:bg-sage-600 text-white px-5 py-3 rounded-xl font-medium">Check</button>
      </form>
      <div id="feedback" class="mt-4"></div>
      <div class="mt-6 text-center">
        <button id="view-full" data-inf="${verb.inf}" class="text-xs text-sage-500 hover:text-sage-700 underline">View full conjugation</button>
      </div>
    </div>
  `;

  const input = app.querySelector("#answer-input");
  input.focus();
  app.querySelector("#answer-form").addEventListener("submit", (e) => {
    e.preventDefault();
    checkAnswer(app, input.value);
  });
  app.querySelector("#view-full").addEventListener("click", (e) =>
    navigate("verb-detail", { inf: e.currentTarget.dataset.inf })
  );
}

function normalize(s) {
  return s
    .trim()
    .toLowerCase()
    .replace(/[áàâã]/g, "a")
    .replace(/[éèê]/g, "e")
    .replace(/[íìî]/g, "i")
    .replace(/[óòôõ]/g, "o")
    .replace(/[úùû]/g, "u")
    .replace(/ç/g, "c");
}

function checkAnswer(app, input) {
  const ts = trainerState;
  const correct = normalize(input) === normalize(ts.current.answer);
  const exact = input.trim() === ts.current.answer;
  ts.attempts++;
  if (correct) ts.score++;

  const fb = app.querySelector("#feedback");
  fb.innerHTML = `
    <div class="rounded-xl p-4 ${correct ? "bg-sage-50 border border-sage-200" : "bg-red-50 border border-red-200"}">
      <p class="font-bold ${correct ? "text-sage-700" : "text-red-700"} mb-1">
        ${correct ? "Correto!" : "Not quite"}
        ${correct && !exact ? '<span class="text-xs font-normal text-sage-500">(watch your accents)</span>' : ""}
      </p>
      <p class="text-sm text-sage-700">Answer: <b>${ts.current.answer}</b> · ${ts.current.verb.inf} (${ts.current.verb.en})</p>
      <div id="srs-slot" class="mt-3"></div>
      <button id="next-btn" class="mt-3 bg-sage-500 hover:bg-sage-600 text-white px-4 py-2 rounded-lg text-sm font-medium">Next →</button>
    </div>
  `;

  // Feed the result into spaced review. A drill is a retrieval attempt, so a
  // correct answer rates "known" and a miss rates "learning" (review.js then
  // rolls the Leitner box). Tracked verbs auto-roll silently; untracked verbs
  // get a quiet opt-in instead of flooding the rotation. Guests see nothing.
  recordDrillReview(app, ts.current.verb, correct);

  const nextBtn = app.querySelector("#next-btn");
  nextBtn.addEventListener("click", () => nextQuestion(app));
  nextBtn.focus();
  app.querySelector("#answer-input").disabled = true;
}

function recordDrillReview(app, verb, correct) {
  if (!isAuthed()) return;
  const id = verbItemId(verb.inf);
  const rating = correct ? "known" : "learning";
  const slot = app.querySelector("#srs-slot");
  if (!slot) return;

  if (srsCtx?.tracked.has(id)) {
    // Already in the workspace — roll the schedule quietly, no UI noise.
    recordReview("verbs", id, rating).catch(() => {});
    slot.innerHTML =
      '<p class="text-xs text-sage-500">Saved to your review schedule.</p>';
    return;
  }

  // Untracked: offer a calm, opt-in on-ramp. No nag if ignored.
  slot.innerHTML = `
    <button id="srs-add" class="text-xs text-sage-600 hover:text-sage-800 underline">
      Add ${verb.inf} to review
    </button>
  `;
  slot.querySelector("#srs-add").addEventListener("click", (e) => {
    e.currentTarget.disabled = true;
    recordReview("verbs", id, rating)
      .then(() => {
        srsCtx?.tracked.add(id);
        slot.innerHTML =
          '<p class="text-xs text-sage-500">Added — it’ll appear in Due for Review.</p>';
      })
      .catch(() => {
        slot.innerHTML =
          '<p class="text-xs text-red-500">Couldn’t add right now.</p>';
      });
  });
}
